import type * as msal from '@azure/msal-browser';
import type { FetchOnboardingStepsResponse } from '@otter-ai/recall-client';
import { OnboardCompleteEvent, trackEvent } from '@otter-ai/telemetry';
import type { ReadonlyURLSearchParams } from 'next/navigation';
import { PATH_URL } from '@/constants/path-constants';
import { NEW_ONBOARDING_STEP } from '@/shared/types/welcome';
import {
  getPartialLoginState,
  resetPartialLoginState,
} from '@/shared/utils/auth';
import { clearSessionStorage } from '@/shared/utils/sessionStorageUtils';

const userDomainFallback = 'your workspace';

export function getDomainString(domains: string[] | undefined | null) {
  return (
    domains?.map((domain: string) => `@${domain}`).join(', ') ||
    userDomainFallback
  );
}

export function getOnboardingStepFromPath(path: string) {
  const welcomePrefix = `/${PATH_URL.WELCOME}/`;
  if (path.startsWith(welcomePrefix)) {
    const stepPart = path.slice(welcomePrefix.length);
    return stepPart && !stepPart.includes('/') ? stepPart : undefined;
  }
  return undefined;
}

export function formatPrice(price: number) {
  return price % 1 === 0 ? price.toString() : price.toFixed(2);
}

/**
 * This property is just typed as a string in telemetry so enumerate possible
 * options here
 */
type NewOnboardingFlow =
  | 'EmailSignUpFlow'
  | 'ThirdPartySignupFlow'
  | 'WorkspaceMemberFlow'
  | 'WorkspaceAdminFlow'
  | 'PostCalendarConnectionFlow'
  | 'SharedSpeechEmailSignupFlow'
  | 'SharedSpeechThirdPartySignupFlow'
  | 'SlackSignupFlow'
  | 'WorkspaceReverseTrialInfoFlow'
  | 'PaidMediaSignUpFlow';

export function getCurrentOnboardingFlow(
  urlParams: URLSearchParams,
): NewOnboardingFlow | undefined {
  return (urlParams.get('new_onboarding') as NewOnboardingFlow) || undefined;
}

/**
 * Utility to help track Microsoft OAuth errors as string.
 * Select specific properties to avoid logging PII that could potentially come back from msft
 */
export function stringifyMicrosoftError(err: msal.AuthError) {
  return `name: ${err.name}, errorCode: ${err.errorCode}, errorMessage: ${err.errorMessage}`;
}

export function getOnboardingSuccessRedirectDestination(
  onboardingData: FetchOnboardingStepsResponse,
  searchParams: ReadonlyURLSearchParams,
) {
  const isOnboardingCompleted =
    onboardingData.next_step === NEW_ONBOARDING_STEP.end_step;

  const redirectUrl = getPartialLoginState()?.redirectQueryParams?.r;

  if (isOnboardingCompleted) {
    trackEvent(OnboardCompleteEvent());
    clearSessionStorage();
    resetPartialLoginState();
  }

  let destination = `/${PATH_URL.HOME}`;

  if (!isOnboardingCompleted) {
    destination = `/${PATH_URL.WELCOME}/${onboardingData.next_step}?${searchParams.toString()}`;
  } else if (redirectUrl) {
    const url = new URL(redirectUrl, window.location.origin);

    destination = url.pathname + url.search;
  }

  return destination;
}
